import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Lock, CheckCircle, Play, ArrowLeft, Clock, FlaskConical, GraduationCap, Microscope, BookOpen, ShieldCheck, ExternalLink, Zap, Crown, ShoppingCart } from 'lucide-react';
import { startCheckout } from '@/lib/razorpay';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_CONFIG = {
  'Research-Based': { icon: FlaskConical, color: 'text-[#09090B] border-[#09090B]/30 bg-[#09090B]/5' },
  'Expert Designed': { icon: GraduationCap, color: 'text-[#09090B] border-zinc-300 bg-zinc-100' },
  'Evidence-Informed': { icon: Microscope, color: 'text-zinc-700 border-zinc-300 bg-zinc-100' },
};

export default function ProgramDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const isPro = user?.subscription?.plan === 'pro' || user?.subscription?.plan === 'enterprise';
  const [program, setProgram] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [validations, setValidations] = useState([]);
  const [loading, setLoading] = useState(true);

  const buyProgram = () => {
    if (!user) {
      const redirectUrl = window.location.origin + window.location.pathname;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    startCheckout({
      type: 'program',
      program_id: id,
      onSuccess: () => {
        if (refreshUser) refreshUser();
        navigate(`/programs/${id}`);
      }
    });
  };

  const subscribePro = () => {
    if (!user) {
      const redirectUrl = window.location.origin + '/pricing';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    startCheckout({
      type: 'pro',
      onSuccess: () => {
        toast.success('Pro activated for 30 days');
        if (refreshUser) refreshUser();
      }
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Critical: program detail must succeed. Sessions/validations are non-critical.
        const progRes = await axios.get(`${API}/programs/${id}`);
        setProgram(progRes.data);
        const [sessRes, valRes] = await Promise.all([
          axios.get(`${API}/programs/${id}/sessions`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/programs/${id}/validations`).catch(() => ({ data: [] }))
        ]);
        setSessions(sessRes.data);
        setValidations(valRes.data);
      } catch (err) {
        console.error('Program detail fetch failed:', err);
        setProgram(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="program-loading">
        <div className="mono-data text-lg animate-pulse-red">Loading...</div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="program-not-found">
        <p className="text-zinc-500">Program not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="program-detail-page">
      {/* Header */}
      <div className="border-b border-[#09090B]">
        <div className="container-swiss py-12 md:py-16">
          <Link
            to="/programs"
            className="mono-data text-sm text-zinc-500 hover:text-[#09090B] transition-colors flex items-center gap-2 mb-8"
            data-testid="back-to-programs"
          >
            <ArrowLeft className="w-3 h-3" /> Back to Programs
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <span className="mono-data text-[#09090B]">{program.category}</span>
            {program.is_free ? (
              <span className="bg-[#09090B] text-white px-3 py-1 mono-data text-xs">Free</span>
            ) : (
              <span className="bg-[#09090B] text-white px-3 py-1 mono-data text-xs flex items-center gap-1">
                <Lock className="w-3 h-3" /> Premium
              </span>
            )}
          </div>

          <h1
            className="text-4xl md:text-5xl font-black tracking-tighter mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="program-title"
          >
            {program.title}
          </h1>
          <p className="text-lg text-zinc-600 max-w-2xl leading-relaxed">
            {program.description}
          </p>
          <div className="flex items-center gap-6 mt-6 mono-data text-sm text-zinc-500">
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {program.session_count} sessions
            </span>
            <span>{program.session_count} hours total</span>
          </div>
        </div>
      </div>

      {/* Pricing Section (Premium programs only) */}
      {!program.is_free && (
        <div className="border-b border-[#09090B]" data-testid="pricing-section">
          <div className="container-swiss py-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#09090B] max-w-3xl">
              {/* Individual Purchase */}
              <div className="p-8 md:border-r border-b md:border-b-0 border-[#09090B]">
                <p className="mono-data text-xs text-zinc-500 mb-3">Individual Purchase</p>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="individual-price">
                    ₹{program.price}
                  </span>
                  <span className="mono-data text-xs text-zinc-400">one-time</span>
                </div>
                <p className="text-sm text-zinc-600 mb-6">Buy this program individually. Lifetime access to all {program.session_count} sessions.</p>
                {!authLoading && isPro ? (
                  <div className="flex items-center gap-2 text-[#09090B] font-bold text-sm" data-testid="pro-access-granted">
                    <Crown className="w-4 h-4" /> You have Pro access to this program
                  </div>
                ) : !authLoading && !isPro ? (
                  <button onClick={buyProgram} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="buy-now-btn">
                    <ShoppingCart className="w-4 h-4" /> Buy Program
                  </button>
                ) : null}
              </div>

              {/* Pro Plan */}
              <div className={`p-8 ${!authLoading && isPro ? 'bg-zinc-100' : 'bg-zinc-50'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-[#09090B]" />
                  <p className="mono-data text-xs text-[#09090B]">Pro Plan Access</p>
                </div>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="pro-price">
                    ₹299
                  </span>
                  <span className="mono-data text-xs text-zinc-400">/month</span>
                </div>
                <p className="text-sm text-zinc-600 mb-2">
                  {!authLoading && isPro ? 'Your Pro plan includes this program.' : 'This program is included with Pro plan.'}
                </p>
                <p className="text-xs text-zinc-400 mb-6">
                  Access 21 premium programs, unlimited sessions, AI coaching, adaptive difficulty · 30 days
                </p>
                {!authLoading && isPro ? (
                  <div className="flex items-center gap-2 text-[#09090B] font-bold text-sm" data-testid="pro-active-badge">
                    <CheckCircle className="w-4 h-4" /> Pro Plan Active
                  </div>
                ) : !authLoading && !isPro ? (
                  <button onClick={subscribePro} className="btn-accent w-full flex items-center justify-center gap-2" data-testid="get-pro-btn">
                    <Crown className="w-4 h-4" /> Get Pro · ₹299 / 30 days
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expert Review Section */}
      {validations.length > 0 && (
        <div className="border-b border-[#09090B]" data-testid="credibility-section">
          <div className="container-swiss py-12">
            <div className="flex items-center gap-2 mb-6">
              <ShieldCheck className="w-5 h-5 text-[#09090B]" />
              <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="credibility-heading">
                {validations[0]?.validator_name ? 'Expert Review' : 'Program Credibility'}
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-[#09090B]">
              {/* Reviewer Profile */}
              <div className="p-8 lg:border-r border-b lg:border-b-0 border-[#09090B]">
                {validations.map((val, i) => (
                  <div key={val.validation_id || i} data-testid={`validator-card-${i}`}>
                    {val.validator_name ? (
                      <>
                        <p className="mono-data text-xs text-zinc-400 mb-4">
                          {val.validation_type === 'Reviewed By' ? 'Reviewed by' : val.validation_type === 'Validated By' ? 'Validated by' : 'Designed by'}
                        </p>
                        <div className="flex items-start gap-5 mb-5">
                          {/* Avatar */}
                          {val.profile_image ? (
                            <img src={val.profile_image} alt={val.validator_name} className="w-16 h-16 border border-[#09090B] object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-16 h-16 bg-[#09090B] text-white flex items-center justify-center text-lg font-bold tracking-wider flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {val.initials || val.validator_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                          )}
                          <div>
                            <h4 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{val.validator_name}</h4>
                            {val.validator_title && <p className="text-sm text-zinc-600 mt-0.5">{val.validator_title}</p>}
                            {val.validator_organization && <p className="mono-data text-xs text-zinc-400 mt-1">{val.validator_organization}</p>}
                            {val.linkedin_url && (
                              <a href={val.linkedin_url} target="_blank" rel="noopener noreferrer" className="mono-data text-xs text-[#09090B] flex items-center gap-1 mt-2">
                                <ExternalLink className="w-3 h-3" /> LinkedIn
                              </a>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-zinc-700 leading-relaxed" data-testid="credibility-bio">{val.bio}</p>
                        {/* Expert Reviewed badge */}
                        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border border-zinc-300 bg-zinc-100 text-[#09090B]">
                          <ShieldCheck className="w-3.5 h-3.5" />
                          <span className="text-xs font-medium tracking-wide">Expert Reviewed</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#09090B]/30 bg-[#09090B]/5 text-[#09090B] mb-4">
                          <FlaskConical className="w-3.5 h-3.5" />
                          <span className="text-xs font-medium tracking-wide">Research-Based</span>
                        </div>
                        <p className="mono-data text-xs text-zinc-500 mb-3">Program designed using:</p>
                        <p className="text-sm text-zinc-700 leading-relaxed" data-testid="credibility-bio">{val.bio}</p>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Methodology */}
              <div className="p-8">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-4 h-4 text-[#09090B]" />
                  <h3 className="mono-data text-sm">Methodology & Techniques</h3>
                </div>
                {validations[0]?.methodology?.length > 0 ? (
                  <ul className="space-y-3" data-testid="methodology-list">
                    {validations[0].methodology.map((method, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 bg-[#09090B] mt-2 flex-shrink-0" />
                        <span className="text-sm text-zinc-700">{method}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-zinc-500">This program is structured using proven cognitive learning techniques.</p>
                )}

                {validations[0]?.credibility_level === 'internal' && (
                  <div className="mt-6 pt-4 border-t border-zinc-200" data-testid="trust-note">
                    <p className="text-xs text-zinc-400 leading-relaxed">
                      This program's content is designed using established research principles.
                      As the platform grows, expert reviews will be added.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sessions List */}
      <div className="container-swiss py-12">
        <h2
          className="text-2xl font-bold tracking-tight mb-8"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="sessions-heading"
        >
          Sessions
        </h2>

        <div className="border-l border-t border-[#09090B]">
          {sessions.map((session, idx) => {
            const canPlay = user && session.is_unlocked && !session.is_completed;
            const isCompleted = session.is_completed;
            const isLocked = !session.is_unlocked;

            return (
              <div
                key={session.session_id}
                className={`border-r border-b border-[#09090B] flex items-center justify-between p-6 ${
                  isLocked ? 'bg-zinc-50 opacity-60' : 'bg-white'
                }`}
                data-testid={`session-row-${session.session_id}`}
              >
                <div className="flex items-center gap-6">
                  <span className="mono-data text-2xl font-black text-zinc-300 w-12">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {session.title}
                    </h3>
                    <p className="mono-data text-xs text-zinc-500 mt-1">
                      {Math.floor(session.duration / 60)} min
                    </p>
                  </div>
                </div>

                <div>
                  {isCompleted ? (
                    <span className="flex items-center gap-2 text-sm font-medium text-[#09090B]">
                      <CheckCircle className="w-4 h-4" /> Completed
                    </span>
                  ) : isLocked ? (
                    <Lock className="w-4 h-4 text-zinc-400" />
                  ) : user ? (
                    <Link
                      to={`/session/${session.session_id}`}
                      className="btn-accent text-xs px-6 py-2 flex items-center gap-2"
                      data-testid={`start-session-${session.session_id}`}
                    >
                      <Play className="w-3 h-3" /> Start
                    </Link>
                  ) : (
                    <button
                      onClick={handleLogin}
                      className="btn-primary text-xs px-6 py-2"
                      data-testid="login-to-start"
                    >
                      Sign In to Start
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
